import { buildWebSocketUrl } from "@/utils/wsAuth";

const TARGET_SAMPLE_RATE = 16_000;

export type LiveTranscriptionMessage =
  | { type: "session_ready"; sessionId: string; modelVersion?: string }
  | { type: "partial"; text: string; startMs?: number; speakerId?: string | null }
  | {
      type: "final";
      utteranceId: string;
      text: string;
      startMs?: number;
      endMs?: number;
      speakerId?: string | null;
      confidence?: number;
    }
  | { type: "error"; code?: string; retryable?: boolean; message?: string }
  | { type: "session_closed"; reason?: string };

export function getTranscriptionLiveWebSocketUrl(): string {
  const base = import.meta.env.VITE_API_BASE_URL || "";

  let wsUrl: string;
  if (base.startsWith("http")) {
    const url = new URL(base);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    const path = url.pathname.replace(/\/+$/, "");
    url.pathname = `${path}/transcription/live`;
    wsUrl = url.toString();
  } else {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    wsUrl = `${proto}//${window.location.host}/api/v1/transcription/live`;
  }

  return buildWebSocketUrl(wsUrl);
}

export function downsampleToPcm16(
  buffer: Float32Array,
  inputSampleRate: number,
  outputSampleRate: number,
): Int16Array {
  if (inputSampleRate === outputSampleRate) {
    return float32ToPcm16(buffer);
  }

  const sampleRateRatio = inputSampleRate / outputSampleRate;
  const newLength = Math.round(buffer.length / sampleRateRatio);
  const result = new Int16Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;

  while (offsetResult < result.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
    let accum = 0;
    let count = 0;
    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i += 1) {
      accum += buffer[i];
      count += 1;
    }
    const sample = count > 0 ? accum / count : 0;
    const clamped = Math.max(-1, Math.min(1, sample));
    result[offsetResult] =
      clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
    offsetResult += 1;
    offsetBuffer = nextOffsetBuffer;
  }

  return result;
}

export function float32ToPcm16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, input[i]));
    out[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return out;
}

type AudioContextCtor = typeof AudioContext;

function getAudioContextClass(): AudioContextCtor | null {
  return (
    window.AudioContext ||
    (window as Window & { webkitAudioContext?: AudioContextCtor })
      .webkitAudioContext ||
    null
  );
}

export interface LiveComposerTranscriptionCallbacks {
  onPartial: (text: string) => void;
  onFinal: (text: string) => void;
  onError: (message: string) => void;
}

/** Streams PCM16 mono 16 kHz audio to `/api/v1/transcription/live`. */
export class LiveComposerTranscriptionSession {
  private ws: WebSocket | null = null;
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private closed = false;

  constructor(private readonly callbacks: LiveComposerTranscriptionCallbacks) {}

  async start(): Promise<void> {
    this.closed = false;
    const wsUrl = getTranscriptionLiveWebSocketUrl();
    const ws = new WebSocket(wsUrl);
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = (action: "resolve" | "reject", error?: Error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        ws.removeEventListener("message", onMessage);
        ws.removeEventListener("error", onError);
        ws.removeEventListener("close", onClose);
        if (action === "resolve") resolve();
        else reject(error ?? new Error("Transcription connection failed."));
      };

      const timeout = window.setTimeout(() => {
        finish("reject", new Error("Timed out connecting to transcription service."));
      }, 15_000);

      const onMessage = (event: MessageEvent) => {
        if (typeof event.data !== "string") return;
        let payload: LiveTranscriptionMessage;
        try {
          payload = JSON.parse(event.data) as LiveTranscriptionMessage;
        } catch {
          return;
        }
        if (payload.type === "session_ready") {
          finish("resolve");
        } else if (payload.type === "error") {
          finish(
            "reject",
            new Error(payload.message || payload.code || "Transcription error"),
          );
        }
      };

      const onError = () => {
        finish("reject", new Error("Could not connect to transcription service."));
      };

      const onClose = (event: CloseEvent) => {
        if (event.code === 1000) return;
        finish(
          "reject",
          new Error(event.reason || "Transcription connection closed."),
        );
      };

      ws.addEventListener("message", onMessage);
      ws.addEventListener("error", onError);
      ws.addEventListener("close", onClose);
    });

    ws.onmessage = (event) => {
      if (typeof event.data !== "string") return;
      let payload: LiveTranscriptionMessage;
      try {
        payload = JSON.parse(event.data) as LiveTranscriptionMessage;
      } catch {
        return;
      }

      if (payload.type === "partial" && payload.text) {
        this.callbacks.onPartial(payload.text);
      } else if (payload.type === "final" && payload.text) {
        this.callbacks.onFinal(payload.text);
      } else if (payload.type === "error") {
        this.callbacks.onError(
          payload.message || payload.code || "Transcription error",
        );
      }
    };

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.stream = stream;

    const AudioCtx = getAudioContextClass();
    if (!AudioCtx) {
      throw new Error("AudioContext is not supported in this browser.");
    }

    const audioContext = new AudioCtx();
    this.audioContext = audioContext;
    const source = audioContext.createMediaStreamSource(stream);
    this.source = source;
    const processor = audioContext.createScriptProcessor(4096, 1, 1);
    this.processor = processor;

    processor.onaudioprocess = (event) => {
      if (this.closed || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      const input = event.inputBuffer.getChannelData(0);
      const pcm = downsampleToPcm16(
        input,
        audioContext.sampleRate,
        TARGET_SAMPLE_RATE,
      );
      this.ws.send(pcm.buffer);
    };

    source.connect(processor);
    processor.connect(audioContext.destination);
  }

  async stop(): Promise<void> {
    this.closed = true;

    if (this.processor) {
      this.processor.onaudioprocess = null;
      this.processor.disconnect();
      this.processor = null;
    }
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    if (this.audioContext) {
      await this.audioContext.close().catch(() => undefined);
      this.audioContext = null;
    }
    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        track.stop();
      }
      this.stream = null;
    }

    const ws = this.ws;
    this.ws = null;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "control", action: "stop" }));
      ws.close(1000, "client_stop");
    } else if (ws) {
      ws.close();
    }
  }
}
